// General
import "./buy-coin-dialog.scss";
import { useEffect } from "react";
// Services
import {
  useLazyGetProfileQuery,
  useLazyGetPaymentGatewayListQuery,
} from "../../../../services/data.service";
// Static Data
import routeConst from "../../../../const/routeConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateAddPaymentMethodDialog,
  updateBuyCoinsDialog,
  updateEkycPendingDialog,
} from "../../../../redux/store/dialogStore";
// react-gtm-module
import TagManager from "react-gtm-module";
// Material UI
import { Dialog, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// i18next
import { useTranslation, Trans } from "react-i18next";
// Custom Hooks
// import useSecurionPaymentFlow from "../../../utility/custom-hooks/useSecurionPaymentFlow-hook";
import useCentrobillCoinsQuickPayment from "../../../utility/custom-hooks/useCentrobillCoinsQuickPayment-hook";
import useGenesisCoinsQuickPayment from "../../../utility/custom-hooks/useGenesisCoinsQuickPayment-hook";
import useCardpayCoinsQuickPayment from "../../../utility/custom-hooks/useCardpayCoinsQuickPayment-hook";
import useCustomNavigate from "../../../utility/custom-hooks/useCustomNavigate-hook";
import IconManager from "../../../utility/manager/icon-manager/icon-manager";

const BuyCoinDialog = () => {
  const isMobile = useMediaQuery("(max-width: 720px)");
  // API variables
  const [
    getProfile,
    {
      data: getProfileData,
      error: getProfileErrorData,
      isFetching: getProfileFetching,
      isLoading: getProfileLoading,
      isSuccess: getProfileSuccess,
      isError: getProfileError,
    },
  ] = useLazyGetProfileQuery();
  const [
    getPaymentGatewayList,
    {
      data: paymentGatewayListData,
      error: paymentGatewayListErrorData,
      isFetching: paymentGatewayListFetching,
      isLoading: paymentGatewayListLoading,
      isSuccess: paymentGatewayListSuccess,
      isError: paymentGatewayListError,
    },
  ] = useLazyGetPaymentGatewayListQuery();

  // Redux variables
  const buyCoinsDialog = useSelector((state) => state.dialog.buyCoinsDialog);
  const selectedCoinPackage = useSelector(
    (state) => state.products.selectedCoinPackage
  );
  const genesisCardDetail = useSelector(
    (state) => state.payment.genesisCardDetail
  );
  const cardpayCardDetail = useSelector(
    (state) => state.payment.cardpayCardDetail
  );
  const centrobillCardDetail = useSelector(
    (state) => state.payment.centrobillCardDetail
  );
  const dispatch = useDispatch();

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  // const securionPaymentFlow = useSecurionPaymentFlow();
  const centrobillQuickPayment = useCentrobillCoinsQuickPayment();
  const genesisQuickPayment = useGenesisCoinsQuickPayment();
  const cardpayQuickPayment = useCardpayCoinsQuickPayment();
  const onNavigate = useCustomNavigate();
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!buyCoinsDialog) return;

    getProfile(null, true);
    getPaymentGatewayList("?product_type=coin", true);

    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-BuyCoin-Dialog",
      },
    });
  }, [buyCoinsDialog]);

  // Event Handlers | Button
  const onPayNow = () => {
    if (getProfileData?.data?.verifications?.ekyc?.pending_approval) {
      dispatch(updateEkycPendingDialog(true));
    } else if (
      !getProfileData?.data?.verifications?.ekyc?.verified &&
      getProfileData?.data?.verifications?.ekyc?.verification_required
    ) {
      onNavigate(routeConst.verify.ekyc.onboarding.path);
      onCloseDialog();
    } else {
      if (paymentGatewayListData?.data?.quick_payment_provider === "genesis") {
        if (genesisCardDetail?.length > 0) {
          genesisQuickPayment();
        }
      } else if (
        paymentGatewayListData?.data?.quick_payment_provider === "cardpay"
      ) {
        if (cardpayCardDetail?.length > 0) {
          cardpayQuickPayment();
        }
      } else if (
        paymentGatewayListData?.data?.quick_payment_provider === "centrobill"
      ) {
        if (centrobillCardDetail?.length > 0) {
          centrobillQuickPayment();
        }
      }
    }
  };
  const onUseDifferentCard = () => {
    onCloseDialog();

    // Route based
    // onNavigate(routeConst.payment.add.path);

    // Dialog based
    dispatch(updateAddPaymentMethodDialog(true));
  };

  // Event Handlers | MUI Dialog
  const onCloseDialog = () => {
    dispatch(updateBuyCoinsDialog(false));
  };

  // Utility Functions
  const returnProvider = () => {
    if (paymentGatewayListData?.data?.quick_payment_provider === "genesis") {
      return genesisCardDetail?.[0]?.last4 || "XXXX";
    } else if (
      paymentGatewayListData?.data?.quick_payment_provider === "cardpay"
    ) {
      return cardpayCardDetail?.[0]?.last4 || "XXXX";
    } else if (
      paymentGatewayListData?.data?.quick_payment_provider === "centrobill"
    ) {
      return centrobillCardDetail?.[0]?.last4 || "XXXX";
    }

    return "XXXX";
  };

  return (
    <Dialog
      className={
        isMobile ? "buy-coin-fullscreen-dialog" : "custom-radius10-dialog"
      }
      fullScreen={isMobile}
      aria-labelledby="buy-coin-confirm-title"
      maxWidth={false}
      PaperProps={{ sx: isMobile ? undefined : { width: 460, height: 720 } }}
      open={buyCoinsDialog}
      onClose={onCloseDialog}
    >
      <div id="buy-coin-dialog">
        <div className="dialog-header-container">
          <button
            className="back-button"
            type="button"
            onClick={onCloseDialog}
            aria-label={t("common.back", "Back")}
          >
            <ArrowBackIcon />
          </button>
          <div className="header-label">{t("profile.wallet")}</div>
          <div aria-hidden="true" />
        </div>

        <div className="purchase-content">
          <div className="top-container">
            <div className="confirmation-icon-container" aria-hidden="true">
              {getIcon("sbCoinIcon", "confirmation-coin-icon")}
              <span className="confirmation-tick-container">
                {getIcon("tickIcon", "confirmation-tick-icon")}
              </span>
            </div>

            <div id="buy-coin-confirm-title" className="confirm-purchase-title">
              {t("purchase.confirm_purchase", "Confirm Purchase")}
            </div>

            <div className="purchase-summary">
              {getIcon("sbCoinIcon", "summary-coin-icon")}
              <div className="purchase-details">
                <div className="coin-amount">
                  {selectedCoinPackage?.quantity?.toLocaleString() || 0}{" "}
                  {t("upgrade.coins")}
                </div>
                <p className="charge-description">
                  {t("purchase.pay_desc_with_last4digit", {
                    last4digit: returnProvider(),
                    amount: `${selectedCoinPackage?.currency || "-"}${
                      (selectedCoinPackage?.discounted_amount ??
                        selectedCoinPackage?.amount) ||
                      "-"
                    }`,
                  })}
                </p>
              </div>
            </div>

          </div>

          <div className="bottom-container">
            <div className="payment-method-container">
              <div className="payment-method-label">
                {t("purchase.payment_method", "Payment method")}
              </div>

              <div className="payment-badges">
                {getIcon("discreteBadge", "discrete-badge")}
                {getIcon("securityBadge", "security-badge")}
                {getIcon("sslBadge", "ssl-badge")}
              </div>
            </div>
            <div className="purchase-buttons-container">
              <button className="pay-button" type="button" onClick={onPayNow}>
                {t("purchase.pay_now")}
              </button>
              <button
                className="different-card-button"
                type="button"
                onClick={onUseDifferentCard}
              >
                {t("purchase.select_new_payment_option")}
              </button>
            </div>

            <footer className="payment-terms">
              <Trans
                i18nKey="purchase.payment_terms"
                defaults="By proceeding with payment, you agree to this platform's <terms>Terms & Condition</terms>."
                components={{
                  terms: (
                    <a
                      href={routeConst.terms.pathAlt}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms &amp; Condition
                    </a>
                  ),
                }}
              />
            </footer>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BuyCoinDialog;
